import Plotly from 'plotly.js';
import { exportComparisonTableToExcel } from '@/modules/shared/DataLayers/utils';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface ExcelExportParams {
  sectionName?: string;
  sectionKey: string;
  sensors: string[];
  verticalLines: VerticalLine[];
  enabledSensors: Record<string, boolean>;
  sensorColors: Record<string, string>;
  showPercentage?: boolean;
  showPercentageBySection?: Record<string, boolean>;
  benchmarkLineId?: string | null;
  benchmarkLineBySection?: Record<string, string | null>;
  syncLegendComparisonTable?: boolean;
  chartData?: any; // Full chart data for time-series export
  xAxisType?: 'date' | 'numeric'; // Type of x-axis
  xAxisLabel?: string; // Label for x-axis (e.g., "Time", "Frequency (Hz)", "RPM")
  selectedVerticalLineIds?: string[]; // IDs of selected vertical lines for highlighting
  chartDivId?: string; // ID of the Plotly chart div for image capture
}

/**
 * Creates an Excel export function (NOT a hook - can be called in callbacks)
 * Handles both synced and per-section percentage/benchmark states
 * Supports both marker-based comparison and full time-series data export
 */
export function createExcelExport({
  sectionName,
  sectionKey,
  sensors,
  verticalLines,
  enabledSensors,
  sensorColors,
  showPercentage,
  showPercentageBySection,
  benchmarkLineId,
  benchmarkLineBySection,
  syncLegendComparisonTable = true,
  chartData,
  xAxisType = 'date',
  xAxisLabel = 'Time',
  selectedVerticalLineIds = [],
  chartDivId,
}: ExcelExportParams) {
  const handleDownload = async () => {
    console.log('[useExcelExport] handleDownload called', {
      sectionName: sectionName || sectionKey,
      sectionKey,
      sensorsCount: sensors.length,
      verticalLinesCount: verticalLines.length,
      hasChartData: !!chartData,
      enabledSensorsCount: Object.values(enabledSensors).filter(Boolean).length,
      chartDivId,
    });

    // Capture chart image if chartDivId is provided and chart exists
    let chartImageBase64: string | undefined;
    if (chartDivId && chartData) {
      try {
        console.log('[useExcelExport] Capturing chart image from div:', chartDivId);
        const chartElement = document.getElementById(chartDivId);

        if (chartElement) {
          // Use Plotly.toImage to capture the chart as PNG
          const imageDataUrl = await Plotly.toImage(chartElement, {
            format: 'png',
            width: 1200,
            height: 700,
          });

          // Extract base64 data from data URL (remove "data:image/png;base64," prefix)
          chartImageBase64 = imageDataUrl.split(',')[1];
          console.log('[useExcelExport] Chart image captured successfully');
        } else {
          console.warn('[useExcelExport] Chart element not found:', chartDivId);
        }
      } catch (error) {
        console.error('[useExcelExport] Failed to capture chart image:', error);
      }
    }

    // Determine effective percentage state (synced vs per-section)
    const effectiveShowPercentage = syncLegendComparisonTable
      ? showPercentage
      : showPercentageBySection?.[sectionKey] ?? true;

    // Determine effective benchmark (synced vs per-section)
    const effectiveBenchmarkId = syncLegendComparisonTable
      ? benchmarkLineId
      : benchmarkLineBySection?.[sectionKey];

    await exportComparisonTableToExcel({
      sectionName: sectionName || sectionKey,
      sensors,
      verticalLines,
      enabledSensors,
      sensorColors,
      showPercentage: effectiveShowPercentage,
      benchmarkLineId: effectiveBenchmarkId,
      chartData,
      xAxisType,
      xAxisLabel,
      selectedVerticalLineIds,
      chartImageBase64,
    });
  };

  return handleDownload;
}
